import { apiFetch } from "../lib/api";
import type { PnlSummaryRow } from "./pnl";

export interface DashboardKpis {
  total_pnl_today: string;
  realized_today: string;
  realized_all_time: string;
  unrealized_live: string;
  unrealized_paper: string;
  open_positions: number; // live
  open_positions_paper: number;
  invested_live: string;
  invested_paper: string;
  running_deployments: number;
  deployments_total: number;
}

export interface ActiveDeploymentSnapshot {
  id: string;
  name: string;
  strategy_key: string;
  mode: "paper" | "live";
  status: string;
  unrealized_pnl: string;
  realized_pnl: string;
  /** Realized over the whole IST day, across earlier runs of today. */
  day_realized_pnl?: string;
  open_positions: number;
  ticks_processed: number;
  bars_processed: number;
}

export interface DashboardSummary {
  kpis: DashboardKpis;
  broker_breakdown: PnlSummaryRow[];
  active_deployments: ActiveDeploymentSnapshot[];
}

export interface PnlTimeseriesPoint {
  ts: string;
  realized_pnl: string;
  unrealized_pnl: string;
  total_pnl: string;
  is_live: boolean;
}

export interface ActivityEvent {
  type: "fill" | "order_rejected" | "deployment_error" | "admin_action";
  ts: string;
  deployment_id: string;
  deployment_name: string;
  severity: "info" | "warn" | "error";
  summary: string;
  symbol?: string | null;
  side?: string | null;
  quantity?: string | null;
  price?: string | null;
  pnl_delta?: string | null;
}

/** Paper / live filter shared by every dashboard endpoint; omitted = both. */
export type DashboardMode = "paper" | "live";

export async function getDashboardSummary(
  mode?: DashboardMode,
): Promise<DashboardSummary> {
  return apiFetch<DashboardSummary>(
    `/dashboard-summary${mode ? `?mode=${mode}` : ""}`,
  );
}

export interface PnlTimeseriesOpts {
  bucket?: "1h" | "1d";
  mode?: DashboardMode;
  broker?: "zerodha" | "dhan" | "delta";
  from?: string;
  to?: string;
}

export async function getPnlTimeseries(
  opts: PnlTimeseriesOpts = {},
): Promise<PnlTimeseriesPoint[]> {
  const params = new URLSearchParams();
  if (opts.bucket) params.set("bucket", opts.bucket);
  if (opts.mode) params.set("mode", opts.mode);
  if (opts.broker) params.set("broker", opts.broker);
  if (opts.from) params.set("from", opts.from);
  if (opts.to) params.set("to", opts.to);
  const qs = params.toString();
  return apiFetch<PnlTimeseriesPoint[]>(`/pnl-timeseries${qs ? "?" + qs : ""}`);
}

export async function getActivityFeed(
  limit: number = 50,
  mode?: DashboardMode,
): Promise<ActivityEvent[]> {
  return apiFetch<ActivityEvent[]>(
    `/activity-feed?limit=${limit}${mode ? `&mode=${mode}` : ""}`,
  );
}
